import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Bug, Send, Loader2, GitBranch, DollarSign } from 'lucide-react';
import { useDebugSession } from '../hooks/useDebugSession';
import { MessageBubble } from '../components/MessageBubble';
import { DebugTurnCard } from '../components/DebugTurnCard';
import { SessionControls } from '../components/SessionControls';
import { StatusBadge } from '../components/StatusBadge';
import { ModeSelector, type MessageMode } from '../components/ModeSelector';
import { AudioRecorder } from '../components/AudioRecorder';
import { FileUploader } from '../components/FileUploader';
import { api } from '../lib/api';

const MODE_STORAGE_KEY = 'studio-agent:message-mode';

function getInitialMode(): MessageMode {
  if (typeof window === 'undefined') return 'ask';
  const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  if (stored === 'ask' || stored === 'plan' || stored === 'implement') return stored;
  return 'ask';
}

export function SessionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const sessionId = id || null;
  const { detail, messages, audits, loading, error, isLive, status, reload } =
    useDebugSession(sessionId);

  const [debugMode, setDebugMode] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [mode, setMode] = useState<MessageMode>(getInitialMode);

  // Persist mode na localStorage para a próxima sessão
  useEffect(() => {
    try {
      window.localStorage.setItem(MODE_STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, [mode]);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll para a última mensagem ao mudar
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  if (!sessionId) return null;

  if (loading && !detail) {
    return (
      <div className="flex items-center justify-center py-20 text-text-muted">
        <Loader2 size={20} className="animate-spin mr-2" /> Carregando sessão…
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Link to="/" className="text-sm text-text-muted hover:text-text inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={14} /> Voltar
        </Link>
        <div className="card border-red-300 bg-red-50 text-red-700 text-sm">
          {error || 'Sessão não encontrada'}
        </div>
      </div>
    );
  }

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    setSendError(null);
    try {
      await api.sendMessage(sessionId, input.trim(), mode);
      setInput('');
      setTimeout(reload, 500);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  };

  const meta = detail.meta;
  const liveStatus = status || meta.status;

  // Intercala audits no fluxo de mensagens quando debugMode está ativo
  const renderItems: Array<
    | { kind: 'message'; ts: string; data: (typeof messages)[number] }
    | { kind: 'audit'; ts: string; data: (typeof audits)[number] }
  > = [];
  for (const m of messages) renderItems.push({ kind: 'message', ts: m.ts, data: m });
  if (debugMode) {
    for (const a of audits) renderItems.push({ kind: 'audit', ts: a.ts, data: a });
  }
  renderItems.sort((a, b) => a.ts.localeCompare(b.ts));

  return (
    <div className="h-full flex flex-col max-w-4xl mx-auto w-full">
      {/* Header */}
      <header className="px-4 py-3 border-b border-border bg-bg-card">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/" className="text-text-muted hover:text-text">
            <ArrowLeft size={16} />
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="font-medium truncate">{meta.specTitle || 'Sem spec'}</h1>
            <div className="flex items-center gap-2 text-xs text-text-muted mt-0.5">
              <span className="font-mono">{meta.sessionId.slice(0, 8)}…</span>
              {meta.branch && (
                <span className="flex items-center gap-1">
                  <GitBranch size={10} />
                  <span className="font-mono">{meta.branch}</span>
                </span>
              )}
              <span className="flex items-center gap-1">
                <DollarSign size={10} />
                {(detail.usage.totalCostUsd ?? 0).toFixed(4)}
              </span>
              <span>
                {(detail.usage.totalInputTokens ?? 0).toLocaleString('pt-BR')}↑ /{' '}
                {(detail.usage.totalOutputTokens ?? 0).toLocaleString('pt-BR')}↓
              </span>
            </div>
          </div>
          <StatusBadge status={liveStatus} />
        </div>

        <div className="flex items-center justify-between gap-3 mt-2">
          <SessionControls sessionId={sessionId} status={liveStatus} onChange={reload} />
          <button
            type="button"
            onClick={() => setDebugMode((v) => !v)}
            className={`btn ${debugMode ? 'bg-blue-600 border-blue-500 text-white' : ''}`}
            title="Mostra system prompt e histórico enviado a cada turno"
          >
            <Bug size={14} />
            Debug {debugMode ? 'ON' : 'OFF'}
          </button>
        </div>
      </header>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
        {renderItems.length === 0 ? (
          <div className="text-center text-text-muted py-10">
            Nenhuma mensagem nesta sessão.
          </div>
        ) : (
          renderItems.map((item) => {
            if (item.kind === 'message') {
              return <MessageBubble key={`m-${item.data.id}`} message={item.data} />;
            }
            return <DebugTurnCard key={`a-${item.data.ts}-${item.data.turnNumber}`} audit={item.data} />;
          })
        )}
      </div>

      {/* Input footer */}
      <footer className="border-t border-border bg-bg-card px-4 py-3 relative">
        {!isLive && (
          <div className="text-xs text-text-muted mb-2">
            Sessão não está ativa — input desabilitado. Use "Reativar" para retomar.
          </div>
        )}
        <div className="flex gap-2 flex-wrap items-center">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={!isLive || sending}
            placeholder={isLive ? 'Digite sua mensagem ou grave um áudio…' : 'Sessão inativa'}
            className="input flex-1 min-w-[200px]"
          />
          <FileUploader
            sessionId={sessionId}
            disabled={!isLive || sending}
            onError={(msg) => setSendError(msg)}
          />
          <AudioRecorder
            disabled={!isLive || sending}
            onTranscribed={(text) => setInput((prev) => (prev ? `${prev} ${text}` : text))}
            onError={(msg) => setSendError(msg)}
          />
          <ModeSelector value={mode} onChange={setMode} disabled={sending} />
          <button
            type="button"
            onClick={handleSend}
            disabled={!isLive || sending || !input.trim()}
            className="btn btn-primary"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Enviar
          </button>
        </div>
        {sendError && <div className="text-xs text-red-600 mt-1">{sendError}</div>}
      </footer>
    </div>
  );
}
